import { History } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IconlyMenuCloseLeft } from "@/core/assets/icons";
import { DS_COLOR } from "@/core/lib/ds-typography";
import { ConversationHistoryPanel } from "./conversation-history-panel";
import { HistoryEditIcon } from "./history-panel-icons";
import type { Conversation } from "../types";

/** Button Icon ghost do DS (40px, pílula) da barra recolhida do histórico. */
const RAIL_BUTTON_CLASS =
  "flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-[var(--woba-color-surface-common-hover)]";

type MenuIaProps = {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  conversations: Conversation[];
  activeConversationId: string | null;
  onNewConversation: () => void;
  onSelectConversation: (id: string) => void;
  onRemoveConversation: (id: string) => void | Promise<void>;
  /** No mobile o painel expandido cobre a tela; a barra recolhida só existe no desktop. */
  mobileOverlay?: boolean;
  "data-testid"?: string;
  "data-testid-rail"?: string;
};

/**
 * Menu-ia (Figma 6051:2746, "histórico de IA"): painel branco de 290px
 * encostado à direita, cantos esquerdos de raio 24. Recolhido, vira a barra de
 * 64px com três ações (nova conversa, expandir e histórico).
 */
export function MenuIa({
  expanded,
  onExpandedChange,
  conversations,
  activeConversationId,
  onNewConversation,
  onSelectConversation,
  onRemoveConversation,
  mobileOverlay = false,
  "data-testid": testId = "menu-ia",
  "data-testid-rail": railTestId = "menu-ia-rail",
}: MenuIaProps) {
  // Sem suspense: o Menu-ia aparece num clique (fechar o chat) e o namespace
  // `action-center` carrega sob demanda; suspender ali derruba a árvore.
  const [translate] = useTranslation(["action-center"], { useSuspense: false });
  const [tCore] = useTranslation(["core"], { useSuspense: false });

  if (expanded) {
    return (
      <aside
        className={
          mobileOverlay
            ? "fixed inset-0 z-50 flex w-full flex-col overflow-hidden md:static md:z-auto md:w-[290px] md:shrink-0 md:rounded-l-3xl"
            : "hidden h-full w-[290px] shrink-0 flex-col overflow-hidden rounded-l-3xl md:flex"
        }
        style={{ backgroundColor: DS_COLOR.backgroundDefault }}
        data-testid={testId}
      >
        <ConversationHistoryPanel
          variant="action-center"
          conversations={conversations}
          activeConversationId={activeConversationId}
          onNewConversation={onNewConversation}
          onSelectConversation={onSelectConversation}
          onRemoveConversation={onRemoveConversation}
          onClose={() => onExpandedChange(false)}
        />
      </aside>
    );
  }

  return (
    <aside
      className="hidden h-full w-16 shrink-0 flex-col items-center gap-2 rounded-l-3xl px-3 py-6 md:flex"
      style={{ backgroundColor: DS_COLOR.backgroundDefault }}
      data-testid={railTestId}
      aria-label={translate("action-center-history-menu") as string}
    >
      <button
        type="button"
        className={RAIL_BUTTON_CLASS}
        onClick={onNewConversation}
        aria-label={tCore("chatbot-history-new-conversation") as string}
        title={tCore("chatbot-history-new-conversation") as string}
      >
        <HistoryEditIcon color={DS_COLOR.contentSubtle} />
      </button>
      <button
        type="button"
        className={RAIL_BUTTON_CLASS}
        onClick={() => onExpandedChange(true)}
        aria-label={translate("action-center-history-open") as string}
        title={translate("action-center-history-open") as string}
      >
        <span
          className="flex items-center justify-center"
          style={{ width: 20, height: 20 }}
        >
          <IconlyMenuCloseLeft size={20} color={DS_COLOR.contentSubtle} />
        </span>
      </button>
      <span
        aria-hidden
        className="my-2 h-px w-full shrink-0"
        style={{ backgroundColor: DS_COLOR.strokeDefault }}
      />
      <button
        type="button"
        className={RAIL_BUTTON_CLASS}
        onClick={() => onExpandedChange(true)}
        aria-label={translate("action-center-history-title") as string}
        title={translate("action-center-history-title") as string}
      >
        <History
          aria-hidden
          color={DS_COLOR.contentSubtle}
          style={{ width: 20, height: 20 }}
        />
      </button>
    </aside>
  );
}
